import type { TEntityId } from "./entity-id";

// NOTE: Mirrors server/src/token-usage/token-usage.types.ts ETokenUsageSortOrder.
export enum ETokenUsageSortOrder {
  ASC = "ASC",
  DESC = "DESC",
}

// NOTE: Mirrors server/src/token-usage/token-usage.types.ts ETokenUsageType.
export enum ETokenUsageType {
  CHAT_TITLE = "chat_title",
  PROMPT = "prompt",
  DEEP_SEARCH_PROMPT = "deep_search_prompt",
  // NOTE: NSAI-6429 — Deep Research v2 bills each stage on its own row, so a
  // run's spend splits into planning / page digestion / compression / writing
  // instead of one DEEP_SEARCH_PROMPT bucket (still used by the pre-v2 path).
  // NOTE: NSAI-6431 — Step 0: the clarification check and the brief.
  DEEP_RESEARCH_SCOPING = "deep_research_scoping",
  DEEP_RESEARCH_PLANNING = "deep_research_planning",
  DEEP_RESEARCH_DIGESTION = "deep_research_digestion",
  DEEP_RESEARCH_RESEARCH = "deep_research_research",
  DEEP_RESEARCH_COMPRESSION = "deep_research_compression",
  DEEP_RESEARCH_WRITING = "deep_research_writing",
  DEEP_RESEARCH_VERIFICATION = "deep_research_verification",
  WEB_SEARCH_PROMPT = "web_search_prompt",
  RE_PROMPT = "re_prompt",
  // NOTE: NSAI-5208 — a generation the user stopped mid-stream; its token counts
  // are usually unknown because the provider reports usage only in the final
  // chunk the abort prevents.
  CANCELLED_PROMPT = "cancelled_prompt",
  FLAGGING = "flagging",
  FLAGGING_RETRIEVAL = "flagging_retrieval",
  PROBE_GENERATION = "probe_generation",
  IMAGE_REPLACEMENT = "image_replacement",
  TOOL_APPROVAL_CONTINUATION = "tool_approval_continuation",
  IMAGE_GENERATION = "image_generation",
  VIDEO_GENERATION = "video_generation",
  CONTEXT_SUMMARY = "context_summary",
  EMBEDDING = "embedding",
  QUERY_REFINEMENT = "query_refinement",
  SEARCH_RESULT_VALIDATION = "search_result_validation",
  AUTOMATION_RUN = "automation_run",
  AUTOMATION_INTEGRATION_VALIDATION = "automation_integration_validation",
  HELP_CHAT = "help_chat",
  SUMMARY = "summary",
  EXTERNAL_API = "external_api",
  PROMPT_IMPROVEMENT = "prompt_improvement",
  // NOTE: Backfilled — three server-side values had drifted from the
  // frontend-shared mirror. Adding them now so the web app's token-
  // usage filter widget shows them as first-class options.
  USER_MEMORY = "user_memory",
  PII_INTENT_CLASSIFICATION = "pii_intent_classification",
  // NOTE: NSAI-3669 — one-off structured call that translates masked entity
  // values (names/orgs/locations) into the requested language on a translation
  // turn, so the answer's egress restore shows translated entities.
  PII_ENTITY_TRANSLATION = "pii_entity_translation",
  DELEGATION = "delegation",
  // NOTE: `CATCH_ME_UP_CLASSIFICATION` was removed in NSAI-3597 —
  // the classifier that used it is gone (context gathering is now
  // LLM-tool-driven).
  PRESENTATION_THEME_GENERATION = "presentation_theme_generation",
  KNOWLEDGE_RETRIEVAL_GATE = "knowledge_retrieval_gate",
  VIDEO_REQUEST_GATE = "video_request_gate",
  YOUTUBE_TRANSCRIPT = "youtube_transcript",
  SCHEDULE_INTERPRETATION = "schedule_interpretation",
  // NOTE: NSAI-5775 — call sites that were billed but never metered until now:
  // presentation visual QA, the Anonymization Lab completion, and the heavy
  // agent-orchestration graph nodes.
  PRESENTATION_QA = "presentation_qa",
  ANONYMIZATION_LAB = "anonymization_lab",
  AGENT_ORCHESTRATION = "agent_orchestration",
}

// NOTE: Mirrors server/src/token-usage/token-usage.types.ts ETokenUsageEndpointType.
export enum ETokenUsageEndpointType {
  CHAT = "chat",
  EMBEDDING = "embedding",
  IMAGE = "image",
  VIDEO = "video",
  MODERATION = "moderation",
  TITLE = "title",
  SUMMARY = "summary",
}

export interface ITokenUsageItem {
  uuid: string;
  type: ETokenUsageType;
  endpointType: ETokenUsageEndpointType | null;
  provider: string | null;
  modelName: string | null;
  // NOTE: NSAI-5216 — the model's catalog display name, i.e. the name the
  // picker showed when it was selected. Null when the usage has no catalog row
  // (ad-hoc env models, hard-deleted models); fall back to `modelName` so a
  // row is never left unlabelled.
  modelTitle: string | null;
  modelUuid: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  reasoningTokens: number | null;
  cachedInputTokens: number | null;
  // NOTE: NSAI-5775 — cache WRITE tokens, the counterpart of `cachedInputTokens`
  // (cache READ). Both are breakdowns of `inputTokens`, not addends.
  cacheWriteInputTokens: number | null;
  totalTokens: number | null;
  durationMs: number | null;
  chatUuid: string | null;
  chatTitle: string | null;
  userMessageUuid: string | null;
  // NOTE: Short preview of the user message that triggered this row. Used as
  // the group header in the token-usage list so a single chat turn collapses
  // its many fan-out rows under one card. Null for background activity rows
  // (automation, help, etc.) that aren't tied to a user message.
  userMessageContent: string | null;
  assistantMessageUuid: string | null;
  projectUuid: string;
  projectTitle: string;
  userUuid: string;
  userFullName: string;
  userEmail: string;
  createdAt: string;
}

// NOTE: A single chat turn (user message → fan-out of RAG, moderation,
// prompt, title, etc.) collapses into one card in the UI. The server builds
// these groups and paginates by group so a chat turn is never split across
// pages.
export interface ITokenUsageUserMessageGroup {
  kind: "user-message";
  userMessageUuid: string;
  userMessageContent: string | null;
  // NOTE: Earliest createdAt across the group — used as the turn's timestamp.
  startedAt: string;
  totalTokens: number;
  // NOTE: NSAI-5161 — null when no item in the group has a recorded duration,
  // so the card renders "—" (unknown) instead of "0 ms".
  totalDurationMs: number | null;
  items: ITokenUsageItem[];
}

// NOTE: Token-usage rows that aren't tied to a user message (automation,
// help, external-api fallback, probe-file processing, etc.) are returned as
// individual entries — each row counts as one pagination unit and renders as
// a flat row in the list instead of being bundled under a single "Background"
// card. That keeps non-chat activity individually visible.
export interface ITokenUsageStandaloneRow {
  kind: "row";
  item: ITokenUsageItem;
}

export type TTokenUsageGroup =
  | ITokenUsageUserMessageGroup
  | ITokenUsageStandaloneRow;

export interface IGetTokenUsageResponse {
  data: TTokenUsageGroup[];
  // NOTE: Total *group* count, not row count. Pagination is by group so a
  // chat turn is never split across pages.
  total: number;
  page: number;
  limitPerPage: number;
}

export interface IGetTokenUsageParams {
  limitPerPage: number;
  page?: number;
  organizationUuid?: string;
  projectUuids?: string;
  userUuids?: string;
  types?: string;
  endpointTypes?: string;
  dateFrom?: string;
  dateTo?: string;
  sort?: ETokenUsageSortOrder;
}

export interface IGetTokenUsageSummaryParams {
  organizationUuid?: string;
  projectUuids?: string;
  userUuids?: string;
  types?: string;
  endpointTypes?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface IGetTokenUsageSummaryResponse {
  totalRecords: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalReasoningTokens: number;
  totalCachedInputTokens: number;
  totalCacheWriteInputTokens: number;
  totalTokens: number;
  totalDurationMs: number;
}

// NOTE: Mirrors server/src/token-usage/token-usage.types.ts. Drives the Token &
// Cost Usage tabs. `organizationUuid` is optional: present → org scope
// (owner-only) for the Organization tab; absent → personal scope (own usage)
// for the Profile tab.
export interface IGetTokenUsageByModelParams {
  organizationUuid?: string;
  projectUuids?: string;
  userUuids?: string;
  types?: string;
  endpointTypes?: string;
  dateFrom?: string;
  dateTo?: string;
}

// NOTE: Mirrors server ETokenUsageExportFormat. The export report format for
// the Token & Cost Usage screen.
export type TTokenUsageExportFormat = "csv" | "xlsx";

// NOTE: Same scope + filter params as the by-model query plus the output
// format. `format` defaults to CSV server-side when omitted.
export interface IExportTokenUsageByModelParams extends IGetTokenUsageByModelParams {
  format?: TTokenUsageExportFormat;
}

// NOTE: One per-model row. Cost fields are null when the model has no
// configured price (its tokens still appear, just without a cost).
export interface ITokenUsageModelCost {
  modelId: TEntityId | null;
  modelUuid: string | null;
  provider: string | null;
  modelName: string | null;
  // NOTE: NSAI-5216 — display name for the row; see ITokenUsageItem.modelTitle.
  modelTitle: string | null;
  // NOTE: NSAI-5220 — false when this usage has no catalog row behind it at all
  // (model_id is null): an internal embedding or guard call the platform made,
  // not a model a user picked. resolveModelPrimaryLabel already keeps such a row
  // labelled, but a label cannot say WHY an unfamiliar name is on a spend
  // screen — this can.
  hasCatalogEntry: boolean;
  records: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  cachedInputTokens: number;
  // NOTE: NSAI-5775 — cache WRITE tokens, a breakdown of `inputTokens` like
  // `cachedInputTokens`. A count only: the catalog carries no cache-write rate,
  // so these stay inside `inputCost` at the full input rate.
  cacheWriteInputTokens: number;
  totalTokens: number;
  // NOTE: inputCost = non-cached input at the full rate; cachedInputCost =
  // cache-read tokens at the discounted rate (null when the model has no cache
  // rate). Disjoint — they sum with outputCost into totalCost.
  inputCost: number | null;
  cachedInputCost: number | null;
  outputCost: number | null;
  totalCost: number | null;
  hasPricing: boolean;
  // NOTE: NSAI-5207 — how many of `records` carry no token counts at all because
  // the provider returned no usage metadata. Their tokens are UNKNOWN, not zero,
  // so a non-zero value makes the token columns a lower bound; when it equals
  // `records` the cost fields above are all null and must render as a dash, never
  // as a confident $0.00.
  recordsMissingTokens: number;
}

// NOTE: NSAI-5211 — one display name and every catalog row reporting under it.
// A name that appears once is a group of one with no subtotal; the repeats are
// what the grouping exists for.
export interface ITokenCostModelGroup {
  label: string;
  models: ITokenUsageModelCost[];
  // NOTE: Null for a single-row group — see buildTokenCostGroups. Where present,
  // its cost fields carry the same "null means unknown" contract as the period
  // totals below.
  subtotal: ITokenUsageByModelTotals | null;
  // NOTE: The same two counts the period totals carry, scoped to this group, so
  // the subtotal can be qualified with the SAME wording rather than a third
  // vocabulary. They answer different questions and must not be merged: this one
  // is "we know the usage but not the rate"...
  incompletelyPricedModelCount: number;
  // ...and this one is "we know the rate but not the usage" (NSAI-5207).
  modelsWithMissingTokensCount: number;
}

export interface IBuildTokenCostGroupsProps {
  models: ITokenUsageModelCost[];
  // NOTE: The label is injected rather than derived here, because resolving it
  // needs a translated fallback for a model with no name at all — and this
  // package stays ignorant of i18next (see the note on TTokenUsageRowTranslate
  // in token-usage-row-view).
  resolveLabel: (model: ITokenUsageModelCost) => string;
}

export interface ITokenUsageByModelTotals {
  records: number;
  recordsMissingTokens: number;
  inputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  cachedInputTokens: number;
  cacheWriteInputTokens: number;
  totalTokens: number;
  // NOTE: NSAI-5151 — null means UNKNOWN, not free, exactly as it does on a
  // model row: nothing in the period carried a rate for that dimension, so the
  // cell renders a dash. The totals row was the one place still summing those
  // nulls as 0, which made a wholly unpriced period report a confident $0.00
  // underneath model rows that all correctly showed a dash.
  //
  // A period with SOME priced usage still returns the known sum — a real lower
  // bound, flagged by `incompletelyPricedModelCount`. A period with no usage at
  // all totals 0, because that genuinely cost nothing.
  inputCost: number | null;
  cachedInputCost: number | null;
  outputCost: number | null;
  totalCost: number | null;
}

export interface IGetTokenUsageByModelResponse {
  currency: string;
  models: ITokenUsageModelCost[];
  totals: ITokenUsageByModelTotals;
  // NOTE: How many returned models understate their cost because a used token
  // dimension has no rate (fully unpriced OR partially priced) — lets the UI
  // flag the money total as a lower bound.
  incompletelyPricedModelCount: number;
  // NOTE: NSAI-5207 — how many returned models understate because at least one
  // of their calls reported no token usage at all. Distinct from the count
  // above: there the rate is missing, here the usage is.
  modelsWithMissingTokensCount: number;
}

// NOTE: Mirrors server ETokenUsageTrendGranularity. Bucket size of the trends
// series. Requested value is a floor — the server may coarsen it and echoes back
// what it actually used.
export enum ETokenUsageTrendGranularity {
  DAY = "day",
  WEEK = "week",
  MONTH = "month",
}

// NOTE: Which measure the trend chart plots. Mirrors server
// ETokenUsageTrendMetric. Every point carries both measures, but the measure is
// still sent with the request: the server folds all but the top few models into
// "Other", and which models those are has to be decided for the measure being
// plotted (NSAI-5258). So switching the toggle does refetch — cheaply, since
// the previous measure stays in the query cache.
export enum ETokenUsageTrendMetric {
  TOKENS = "tokens",
  COST = "cost",
}

// NOTE: Same scope + filter params as the by-model query, plus the bucket size
// and the IANA zone the bucket boundaries are computed in (so "yesterday" on the
// chart matches the viewer's calendar rather than the server's).
export interface IGetTokenUsageTrendsParams extends IGetTokenUsageByModelParams {
  granularity?: ETokenUsageTrendGranularity;
  timezone?: string;
  // NOTE: Selects which models get a named series rather than being folded into
  // "Other"; it does not change the values returned. Omitted means tokens.
  metric?: ETokenUsageTrendMetric;
}

// NOTE: One point of a model's series. `totalCost` is null when the model has no
// usable rate — unknown, not free.
export interface ITokenUsageTrendPoint {
  bucketStart: string;
  records: number;
  inputTokens: number;
  cachedInputTokens: number;
  cacheWriteInputTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  totalCost: number | null;
}

// NOTE: `points` is zero-filled and index-aligned with the response's `buckets`,
// so it can be plotted without reconciling sparse data. `isOther` marks the
// single synthetic series aggregating the models past the server's series cap —
// it has no model identity and the UI labels it from i18n.
export interface ITokenUsageTrendSeries {
  modelId: TEntityId | null;
  modelUuid: string | null;
  provider: string | null;
  modelName: string | null;
  modelTitle: string | null;
  hasPricing: boolean;
  isOther: boolean;
  totalTokens: number;
  totalCost: number | null;
  points: ITokenUsageTrendPoint[];
}

export interface IGetTokenUsageTrendsResponse {
  currency: string;
  // NOTE: The granularity actually used, which may be coarser than requested.
  granularity: ETokenUsageTrendGranularity;
  // NOTE: NSAI-5163 — the IANA zone the buckets were actually computed in. Not
  // necessarily the one requested: a zone this Postgres build does not carry, or
  // a failed catalogue read, degrades to UTC. Since `buckets` below are naive
  // wall-clock strings with no offset, this is the only thing that says what
  // they mean.
  timezone: string;
  // NOTE: Naive wall-clock bucket starts ("YYYY-MM-DDTHH:MM:SS", no offset) in
  // the requested timezone, ascending. Shared x-axis for every series. Parse
  // them as wall-clock values — re-interpreting them as UTC instants and then
  // formatting in local time would shift every label.
  buckets: string[];
  series: ITokenUsageTrendSeries[];
  // NOTE: Period totals across every model, not just the charted series.
  totals: ITokenUsageByModelTotals;
  incompletelyPricedModelCount: number;
  modelsWithMissingTokensCount: number;
  // NOTE: How many models were folded into the "Other" series.
  truncatedModelCount: number;
}
